/**
 * Частота продаж за окно (по умолчанию 7 дней).
 *
 * Нужна для двух вещей кассы: блок «Часто покупают» и подъём самых
 * продаваемых категорий в начало. Вынесено из UI чистыми функциями:
 * правила (окно, вычет возвратов, порог) покрыты тестами.
 */

export const FREQUENCY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000
/** Категория попадает в частотные, только если продано хотя бы столько единиц. */
export const FREQUENCY_MIN_SALES = 3
/** Сколько частотных категорий поднимаем в начало. */
export const FREQUENCY_MAX_TOP = 3
/** Жёсткий предел частотных категорий (даже если подняли порог). */
export const FREQUENCY_HARD_CAP = 8
/** Сколько товаров показываем в «Часто покупают». */
export const FREQUENT_PRODUCTS_LIMIT = 8

export interface SalesLineRef {
  barcode: string
  name?: string
  qty: number
}

export interface SalesDocRef {
  createdAt: number
  /** Возврат прихода: количество вычитается. */
  isReturn: boolean
  items: SalesLineRef[]
}

/** Ключ товара: штрихкод, а для товаров без ШК — нормализованное имя. */
export function productKey(barcode?: string, name?: string): string {
  const code = barcode?.trim()
  if (code) return `b:${code}`
  return `n:${normalizeName(name)}`
}

/** Имена сравниваем без регистра, лишних пробелов и ё/е. */
export function normalizeName(value?: string): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
}

/**
 * Продано единиц за окно по ключу, который вернул `resolveKey`.
 * Возвраты вычитаются, итог не опускается ниже нуля.
 */
export function aggregateFrequency(
  docs: SalesDocRef[],
  resolveKey: (line: SalesLineRef) => string | null,
  now: number = Date.now(),
  windowMs: number = FREQUENCY_WINDOW_MS
): Map<string, number> {
  const since = now - windowMs
  const totals = new Map<string, number>()
  for (const doc of docs) {
    if (doc.createdAt < since || doc.createdAt > now) continue
    const sign = doc.isReturn ? -1 : 1
    for (const item of doc.items) {
      const key = resolveKey(item)
      if (!key) continue
      const qty = Number.isFinite(item.qty) ? item.qty : 0
      totals.set(key, (totals.get(key) ?? 0) + sign * qty)
    }
  }
  for (const [key, qty] of totals) {
    if (qty <= 0) totals.set(key, 0)
  }
  return totals
}

/** Продано единиц по штрихкоду за окно. */
export function aggregateProductFrequency(
  docs: SalesDocRef[],
  now: number = Date.now(),
  windowMs: number = FREQUENCY_WINDOW_MS
): Map<string, number> {
  return aggregateFrequency(docs, (line) => line.barcode?.trim() || null, now, windowMs)
}

/** Свернуть статистику по ключам товаров в статистику по категориям. */
export function rollupByCategory(
  productStats: Map<string, number>,
  keyCategory: Map<string, string>
): Map<string, number> {
  const totals = new Map<string, number>()
  for (const [key, qty] of productStats) {
    if (qty <= 0) continue
    const category = keyCategory.get(key)?.trim()
    if (!category) continue
    totals.set(category, (totals.get(category) ?? 0) + qty)
  }
  return totals
}

export interface CategoryLike {
  name: string
}

export interface OrderedCategories<T extends CategoryLike> {
  list: T[]
  /** Имена, поднятые в начало — пригодятся для подсветки/аналитики. */
  topNames: string[]
}

/**
 * Частотные категории — в начало, остальные сохраняют исходный порядок
 * (sortOrder из импорта). Порядок при равной частоте — исходный.
 */
export function orderCategoriesByFrequency<T extends CategoryLike>(
  list: T[],
  stats: Map<string, number>,
  options: { minSales?: number; maxTop?: number; hardCap?: number } = {}
): OrderedCategories<T> {
  const minSales = options.minSales ?? FREQUENCY_MIN_SALES
  const maxTop = Math.min(options.maxTop ?? FREQUENCY_MAX_TOP, options.hardCap ?? FREQUENCY_HARD_CAP)
  const eligible = list
    .map((item, index) => ({ item, index, qty: stats.get(item.name) ?? 0 }))
    .filter((entry) => entry.qty >= minSales)
    .sort((a, b) => b.qty - a.qty || a.index - b.index)
    .slice(0, maxTop)
  if (eligible.length === 0) return { list: [...list], topNames: [] }
  const topNames = eligible.map((entry) => entry.item.name)
  const topSet = new Set(topNames)
  const rest = list.filter((item) => !topSet.has(item.name))
  return { list: [...topNames.map((name) => list.find((i) => i.name === name)!), ...rest], topNames }
}

export interface FrequentProduct<T extends { barcode: string }> {
  product: T
  qty: number
}

/**
 * Топ товаров по продажам. Товары вне списка `eligible` (архивные,
 * из неактивных категорий) отбрасываются.
 */
export function pickFrequentProducts<T extends { barcode: string; name: string }>(
  products: T[],
  stats: Map<string, number>,
  options: { limit?: number; minSales?: number } = {}
): FrequentProduct<T>[] {
  const limit = options.limit ?? FREQUENT_PRODUCTS_LIMIT
  const minSales = options.minSales ?? 1
  return products
    .map((product) => ({
      product,
      qty: stats.get(productKey(product.barcode, product.name)) ?? 0
    }))
    .filter((entry) => entry.qty >= minSales)
    .sort(
      (a, b) =>
        b.qty - a.qty || a.product.name.localeCompare(b.product.name, 'ru')
    )
    .slice(0, limit)
}
